import React from 'react';
import type {AuthState} from '../contexts/AuthContext.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {canReadRecordActivity, type AuthState as AccessAuthState} from '../lib/access.js';
import CommentsSection, {type CommentRow} from './CommentsSection.tsx';
import RecordActivityLog from './RecordActivityLog.tsx';
import {
  useRecordCollaborationData,
  type RecordCollaborationMentionLoader,
  type RecordCollaborationMentionPolicy,
} from './recordCollaborationData.js';

export default function RecordCollaborationSection({
  sb,
  authState,
  entityType,
  entityId,
  entityLabel,
  spacing = 16,
  showComments = true,
  activityLimit = 50,
  activityEventFilter = null,
  // Optional mention-picker source override handed to the data owner.
  // A custom source must carry its stable audience policy so it cannot reuse
  // the generic mention cache entry.
  loadMentionable = undefined,
  mentionPolicy = undefined,
  legacyNote,
  fullYearDates = entityType === 'pig.breeder',
  recordCreatedAt = undefined,
  recordCreatedBy = undefined,
  recordCreatedByName = undefined,
}: {
  // The five identity props every call site passes; none is optional today, so
  // none is optional here.
  sb: SupabaseClient;
  authState: AuthState;
  entityType: string;
  entityId: string;
  entityLabel: string;
  // Layout + mode knobs. Only the record pages that need a non-default pass
  // these (spacing={0} on the equipment entry pages, showComments={false} on
  // the two activity-only surfaces, a raised activityLimit on those same two).
  spacing?: number;
  showComments?: boolean;
  activityLimit?: number;
  // Audit-row predicate handed straight to RecordActivityLog, which calls it
  // per event and treats a throw as "exclude". `null` means "no filter"; the
  // rows are opaque jsonb from the mig-058 RPCs, so the event stays `unknown`
  // and each caller narrows the fields it tests.
  activityEventFilter?: ((event: unknown) => boolean) | null;
  // Same runtime contract as commentsApi's loadMentionableProfiles. Omitting
  // it selects the generic all-authorized policy; To Do passes the narrower
  // participant-only source and declares that identity below.
  loadMentionable?: RecordCollaborationMentionLoader;
  mentionPolicy?: RecordCollaborationMentionPolicy;
  // Read-only bridge until the separately approved breeder Notes transfer runs.
  legacyNote?: string;
  fullYearDates?: boolean;
  recordCreatedAt?: string | null;
  recordCreatedBy?: string | null;
  recordCreatedByName?: string | null;
}) {
  // Migration 221: comments and record Activity follow _activity_can_read's
  // per-role scope (Light: the record types its screens show; Equipment Tech:
  // Tasks and equipment). Off that scope the server refuses the read and the
  // post, so the section renders nothing and issues no request (P01) rather
  // than an empty panel whose composer errors.
  const readable = canReadRecordActivity(authState as AccessAuthState, entityType);
  // This is intentionally a component-scoped owner, not a route catalog
  // resource. Its registry proves the finite set of record mounts that use it;
  // no blanket 77-surface claim follows from a shared record widget.
  const collaboration = useRecordCollaborationData({
    sb,
    authState,
    entityType: readable ? entityType : null,
    entityId,
    showComments,
    activityLimit,
    loadMentionable,
    mentionPolicy,
    recordCreatedAt,
    recordCreatedBy,
    recordCreatedByName,
  });

  if (!readable) return null;

  return (
    <>
      {showComments && (
        <div data-record-collaboration-section="1" style={{marginTop: spacing}}>
          <CommentsSection
            sb={sb}
            authState={authState}
            entityType={entityType}
            entityId={entityId}
            entityLabel={entityLabel}
            legacyNote={legacyNote}
            fullYearDates={fullYearDates}
            comments={collaboration.comments.data as CommentRow[]}
            commentsLoading={collaboration.comments.loading}
            commentsError={collaboration.comments.error}
            mentionableProfiles={collaboration.mentionableProfiles.data}
            mentionableProfilesError={collaboration.mentionableProfiles.error}
            refreshComments={collaboration.refreshComments}
          />
        </div>
      )}
      <div style={{marginTop: spacing}}>
        <RecordActivityLog
          entityType={entityType}
          entityId={entityId}
          fullYearDates={fullYearDates}
          eventFilter={activityEventFilter}
          events={collaboration.activity.data}
          refreshActivity={collaboration.refreshActivity}
          recordCreatedAt={collaboration.creation.createdAt}
          recordCreatedBy={collaboration.creation.createdBy}
          recordCreatedByName={collaboration.creation.createdByName}
          creationLoading={collaboration.creation.loading}
        />
      </div>
    </>
  );
}
